import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Settings, Mail, Globe, Info, Share2, Star, HelpCircle, Trash, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const APP_VERSION = "1.0.0"; // This should match your app version

export const AppSettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showRatePrompt, setShowRatePrompt] = useState(false);

  const handleContactSupport = () => {
    window.location.href = 'mailto:info@thepeoapp.com';
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'The PEO App',
          text: 'Find the perfect PEO for your business',
          url: window.location.href,
        });
      } else {
        // Fallback for browsers that don't support native sharing
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: "Link copied!",
          description: "The app link has been copied to your clipboard.",
        });
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleRateApp = () => {
    // This would typically open the app store rating page
    // For now, we'll just show a toast
    toast({
      title: "Rate The PEO App",
      description: "Thank you for your feedback!",
    });
    setShowRatePrompt(false);
  };

  const handleExportData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'peo-app-data.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Data exported",
        description: "Your data has been downloaded successfully.",
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting your data.",
        variant: "destructive",
      });
    }
  };

  const handleRequestDataDeletion = () => {
    window.location.href = 'mailto:info@thepeoapp.com?subject=Data%20Deletion%20Request';
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="h-12 w-12">
          <Settings className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Settings</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          <Button 
            variant="outline" 
            className="w-full justify-start" 
            onClick={handleContactSupport}
          >
            <Mail className="mr-2 h-4 w-4" />
            Contact Support
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={() => navigate('/privacy')}
          >
            <Info className="mr-2 h-4 w-4" />
            Privacy Policy
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={() => navigate('/terms')}
          >
            <Globe className="mr-2 h-4 w-4" />
            Terms of Service
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={handleShare}
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share App
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={handleRateApp}
          >
            <Star className="mr-2 h-4 w-4" />
            Rate App
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={handleExportData}
          >
            <Download className="mr-2 h-4 w-4" />
            Export My Data
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={handleRequestDataDeletion}
          >
            <Trash className="mr-2 h-4 w-4" />
            Request Data Deletion
          </Button>

          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground text-center">
              Version {APP_VERSION}
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
